import React, { useState, useEffect } from 'react';
import { X, Sparkles, Gift, Calendar, Tag } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AktionenPopup = ({ pageType }) => {
  const [popup, setPopup] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchPopup = async () => {
      try {
        // Check if user has dismissed popup for this page (24 hours validity)
        const dismissed = localStorage.getItem(`aktionen_popup_dismissed_${pageType}`);
        if (dismissed) {
          const dismissedTime = parseInt(dismissed);
          const now = Date.now();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          
          // If more than 24 hours have passed, clear the dismissal
          if (now - dismissedTime > twentyFourHours) {
            localStorage.removeItem(`aktionen_popup_dismissed_${pageType}`);
          } else {
            return; // Still dismissed
          }
        }

        // Fetch active popup for this page type
        const response = await axios.get(
          `${API_URL}/api/admin/aktionen/public/active-popup`,
          { params: { page_type: pageType } }
        );

        if (response.data) {
          setPopup(response.data);
          
          // Show popup after delay
          const delay = response.data.popup_delay || 10;
          setTimeout(() => {
            setShowPopup(true);
            // Fade in animation
            setTimeout(() => setIsVisible(true), 100);
          }, delay * 1000);
        }
      } catch (error) {
        console.error('Error fetching popup:', error);
      }
    };

    fetchPopup();
  }, [pageType]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShowPopup(false);
      // Remember dismissal for 24 hours
      localStorage.setItem(
        `aktionen_popup_dismissed_${pageType}`,
        Date.now().toString()
      );
    }, 300);
  };

  const handleCTA = () => {
    // Navigate to signup page with coupon code pre-filled
    const couponCode = popup.coupon_code || '';
    if (couponCode) {
      // Store coupon in sessionStorage for signup page
      sessionStorage.setItem('promo_code', couponCode);
      sessionStorage.setItem('promo_details', JSON.stringify(popup));
    }
    window.location.href = '/signup';
  };

  if (!showPopup || !popup) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        opacity: isVisible ? 1 : 0,
      }}
      onClick={handleClose}
    >
      <div
        className="relative max-w-md w-full rounded-xl shadow-2xl transform transition-all duration-300"
        style={{
          backgroundColor: '#1A1F2E',
          border: '2px solid #7CD4FD',
          transform: isVisible ? 'scale(1)' : 'scale(0.9)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors"
          style={{ backgroundColor: '#2A3142', color: '#94A3B8' }}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with Icon */}
        <div className="p-6 pb-4 text-center">
          <div
            className="inline-flex p-4 rounded-full mb-4"
            style={{ backgroundColor: '#7CD4FD20' }}
          >
            <Sparkles className="w-10 h-10" style={{ color: '#7CD4FD' }} />
          </div>
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: '#E6EDF3' }}
          >
            {popup.name}
          </h2>
          <p className="text-sm" style={{ color: '#94A3B8' }}>
            {popup.description}
          </p>
        </div>

        {/* Offer Details */}
        <div className="px-6 pb-6">
          <div className="space-y-3 mb-6">
            {/* Discount */}
            <div
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{ backgroundColor: '#2A3142' }}
            >
              <Tag className="w-5 h-5" style={{ color: '#22C55E' }} />
              <div>
                <p className="text-xs" style={{ color: '#94A3B8' }}>
                  Rabatt
                </p>
                <p className="font-bold" style={{ color: '#E6EDF3' }}>
                  {popup.discount_type === 'percentage'
                    ? `${popup.discount_value}%`
                    : `€${popup.discount_value}`}
                </p>
              </div>
            </div>

            {/* First Month Free */}
            {popup.first_month_free && (
              <div
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: '#22C55E20' }}
              >
                <Gift className="w-5 h-5" style={{ color: '#22C55E' }} />
                <p className="font-semibold" style={{ color: '#22C55E' }}>
                  Erster Monat gratis!
                </p>
              </div>
            )}

            {/* Free Item */}
            {popup.free_item && (
              <div
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: '#2A3142' }}
              >
                <Gift className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                <div>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    Gratis dazu
                  </p>
                  <p className="font-bold" style={{ color: '#E6EDF3' }}>
                    {popup.free_item} (€{popup.free_item_value})
                  </p>
                </div>
              </div>
            )}

            {/* Validity */}
            <div
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{ backgroundColor: '#2A3142' }}
            >
              <Calendar className="w-5 h-5" style={{ color: '#F59E0B' }} />
              <div>
                <p className="text-xs" style={{ color: '#94A3B8' }}>
                  Gültig bis
                </p>
                <p className="font-bold" style={{ color: '#E6EDF3' }}>
                  {new Date(popup.valid_until).toLocaleDateString('de-DE')}
                </p>
              </div>
            </div>

            {/* Limited Spots with Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: '#EF4444' }}>
                  🔥 Fast ausverkauft!
                </span>
                <span className="text-sm font-bold" style={{ color: '#EF4444' }}>
                  {popup.max_customers - (popup.current_customers || 0)} / {popup.max_customers} verfügbar
                </span>
              </div>
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: '#2A3142' }}
              >
                <div 
                  className="h-full transition-all duration-500"
                  style={{ 
                    width: `${((popup.max_customers - (popup.current_customers || 0)) / popup.max_customers) * 100}%`,
                    background: 'linear-gradient(90deg, #EF4444 0%, #EF4444AA 100%)',
                    boxShadow: '0 0 10px #EF444460'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Coupon Code */}
          {popup.coupon_code && (
            <div
              className="mb-4 p-3 rounded-lg text-center border-2 border-dashed"
              style={{ borderColor: '#7CD4FD', backgroundColor: '#7CD4FD10' }}
            >
              <p className="text-xs mb-1" style={{ color: '#94A3B8' }}>
                Gutscheincode:
              </p>
              <p
                className="text-xl font-mono font-bold"
                style={{ color: '#7CD4FD' }}
              >
                {popup.coupon_code}
              </p>
            </div>
          )}

          {/* CTA Button */}
          <Button
            onClick={handleCTA}
            className="w-full py-6 text-lg font-bold"
            style={{
              background: 'linear-gradient(90deg, #7CD4FD 0%, #22C55E 100%)',
              color: '#0B0F14',
            }}
          >
            Jetzt sichern! →
          </Button>

          {/* Small Print */}
          {popup.requires_12_month && (
            <p className="text-xs text-center mt-3" style={{ color: '#94A3B8' }}>
              * Nur für 12-Monats-Abonnements gültig
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
